import i18next from 'i18next';
import { GET_URL, QR_IMAGE } from '@/game/qrPromo';
import { sealFromPhaser } from '@/game/ui/domInput';

/**
 * A QR code sized for a promo card — the `extra` block the "did you know" outro variant uses in
 * place of the store badge row (src/game/ui/clipOutro.ts).
 *
 * **Why a card ever shows a QR instead of badges.** The badges are the right ask on the device
 * that can act on them; on a desktop they open a store page on a machine that cannot install a
 * phone app, and the purchase then has to survive being remembered until the parent next picks up
 * their phone. That is the gap src/game/qrPromo.ts was written to close, and this is the same
 * code, the same URL and the same reasoning — borrowed by a full-size card rather than a corner
 * one. Which means the same rule applies: the caller shows this only where `isDesktopBrowser()`
 * is true, because nobody scans their own phone.
 *
 * One code, resolved a device later, exactly as the corner card does — see `GET_URL` for why
 * there is not one per store.
 *
 * **This block does not lay itself out.** It is the code and its caption and nothing more; where
 * they sit is `.promo-card--qr-side` in index.html, which puts the whole thing in a second column
 * beside the card's text. That is a height rule — stacked under three body lines this card runs
 * 553-599 units against a budget of 524 and lands on the Replay button (promoCard.placement.test.ts
 * carries the numbers). Keeping the layout there rather than here means the one file that knows
 * about BOTTOM_LIMIT is the one that decides it.
 */

/**
 * Side of the code in game units. See `.promo-card__qr-code` for why it is drawn on white.
 *
 * Bounded from BELOW by scanning and from ABOVE by the card's height budget, and the two are close
 * enough together that this is not a number to nudge by eye. The corner card's 88px is the floor
 * the densest of our codes needs to stay above ~2px per module (qrPromo.ts); the ceiling is
 * BOTTOM_LIMIT, which this card is measured against in every language by
 * clip-outro.height.test.ts. Change it and run that test.
 */
const QR_SIZE = 104;

/**
 * The block. `sealFromPhaser` because it contains a real link: without it a tap on the code also
 * lands on the screen behind the card, which on Summary means "quit to the menu".
 */
export function createCardQr(): HTMLElement {
  const row = document.createElement('span');
  row.className = 'promo-card__qr';
  sealFromPhaser(row);

  const link = document.createElement('a');
  link.className = 'promo-card__qr-link';
  // The same URL the code encodes, so a desktop visitor who clicks rather than scans lands where
  // the scan would have. No `trackStoreClick`: that event names a store and this card does not
  // know one — see the same note in qrOverlay.ts.
  link.href = GET_URL;
  link.target = '_blank';
  link.rel = 'noopener';

  const img = document.createElement('img');
  img.className = 'promo-card__qr-code';
  img.src = QR_IMAGE;
  img.alt = '';   // decorative: the caption beside it says what this is, and a QR has no readable alt
  img.style.width = `${QR_SIZE}px`;
  img.style.height = `${QR_SIZE}px`;
  link.append(img);

  const hint = document.createElement('span');
  hint.className = 'promo-card__qr-hint';
  hint.textContent = i18next.t('qr.scan');
  link.append(hint);

  link.setAttribute('aria-label', `${i18next.t('qr.title')} — ${i18next.t('qr.scan')}`);
  row.append(link);
  return row;
}
